import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { getHiddenDrugs, setDrugHidden, type HiddenDrug } from "@/lib/api";

/**
 * The signed-in doctor's medicines hidden from drug search, shared by every
 * DrugSearchInput and Settings > My Medicines so hiding / unhiding anywhere
 * updates all of them. Loaded once per doctor.
 */

let cache: HiddenDrug[] = [];
let cacheDoctorId: string | null = null;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

function setCache(next: HiddenDrug[]) {
  cache = next;
  listeners.forEach((l) => l());
}

function ensureLoaded(doctorId: string) {
  if (cacheDoctorId === doctorId) return;
  cacheDoctorId = doctorId;
  cache = [];
  const request = getHiddenDrugs()
    .then((drugs) => { if (loading === request) setCache(drugs); })
    .catch(() => {});
  loading = request;
}

export async function hideDrug(drug: HiddenDrug) {
  const previous = cache;
  setCache([...cache.filter((d) => d.id !== drug.id), drug]);
  try { await setDrugHidden(drug.id, true); }
  catch (e) { setCache(previous); throw e; }
}

export async function unhideDrug(drugId: number) {
  const previous = cache;
  setCache(cache.filter((d) => d.id !== drugId));
  try { await setDrugHidden(drugId, false); }
  catch (e) { setCache(previous); throw e; }
}

export function useHiddenDrugs(): HiddenDrug[] {
  const { currentUser } = useAuth();
  const doctorId = currentUser?.doctor_id || currentUser?.id || "";
  const [, rerender] = useState(0);

  useEffect(() => {
    const listener = () => rerender((n) => n + 1);
    listeners.add(listener);
    if (doctorId) ensureLoaded(doctorId);
    return () => { listeners.delete(listener); };
  }, [doctorId]);

  return cacheDoctorId === doctorId ? cache : [];
}
